import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { Badge, Button, Input } from "../lib/ui.js";

interface Row {
  business_id: string;
  business_name: string;
  client_name: string;
  offered: boolean;
  enabled: boolean;
  slug: string | null;
  bookings_30d: number;
  codes_30d: number;
  credits_30d: number;
}

const ERRORS: Record<string, string> = {
  web_booking_slug_invalid: "The address can use lowercase letters, numbers and single dashes, 3 to 40 characters.",
  web_booking_slug_taken: "Another business already uses that address.",
};

/**
 * Admin > Online booking. The platform OFFERS online booking to a business and sets its page address (/book/<address>); the business's owner
 * then turns it on and chooses what customers can book (Business settings > Online booking). Each code text costs a Texto credit, so the
 * credits used in the last 30 days are shown per business.
 */
export function WebBooking() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const { data, error: err } = await supabase.rpc("rpc_admin_web_booking_overview");
    if (err) setError(err.message);
    setRows((data ?? []) as Row[]);
    setLoading(false);
  }
  useEffect(() => {
    void load();
  }, []);

  if (loading) return <p className="rc-loading">Loading...</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h1 className="rc-h1" style={{ margin: 0 }}>Online booking</h1>
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "4px 0 0", maxWidth: 700 }}>
          A web page where customers book, change and cancel. A customer's mobile is checked by a texted code (one code covers bookings for 20
          minutes; every cancellation or change needs its own). Offer it here, then the business's owner turns it on.
        </p>
      </div>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r) => (
          <Item key={r.business_id} row={r} onChanged={load} />
        ))}
        {rows.length === 0 && <p style={{ color: "var(--rc-text-faint)" }}>No businesses yet.</p>}
      </div>
    </div>
  );
}

function Item({ row, onChanged }: { row: Row; onChanged: () => Promise<void> }) {
  const [offered, setOffered] = useState(row.offered);
  const [slug, setSlug] = useState(row.slug ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const changed = offered !== row.offered || (slug.trim() !== (row.slug ?? "") && slug.trim() !== "");

  async function save() {
    setBusy(true);
    setNote(null);
    const { error } = await supabase.rpc("rpc_admin_set_web_booking", { p_business_id: row.business_id, p_offered: offered, p_slug: slug.trim() || null });
    setBusy(false);
    if (error) {
      setNote({ ok: false, text: ERRORS[error.message] ?? error.message });
      return;
    }
    setNote({ ok: true, text: "Saved." });
    await onChanged();
  }

  const link = row.slug ? `${window.location.origin}/book/${row.slug}` : null;

  return (
    <div className="rc-card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div>
          <strong>{row.business_name}</strong>
          <div style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>{row.client_name}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {row.offered ? <Badge tone="success">Offered</Badge> : <Badge>Not offered</Badge>}
          {row.enabled ? <Badge tone="primary">Turned on</Badge> : row.offered ? <Badge tone="warning">Owner has not turned it on</Badge> : null}
        </div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5 }}>
          <input type="checkbox" checked={offered} onChange={(e) => setOffered(e.target.checked)} /> Offer online booking
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "var(--rc-text-muted)" }}>
          Page address (/book/...)
          <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="chosen from the business name" style={{ width: 240 }} />
        </label>
        <Button variant="primary" disabled={busy || !changed} onClick={() => void save()}>{busy ? "Saving..." : "Save"}</Button>
        {note && <span style={{ fontSize: 13, color: note.ok ? "var(--rc-success)" : "var(--rc-danger)" }}>{note.text}</span>}
      </div>
      {link && (
        <div style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>
          <a href={link} target="_blank" rel="noreferrer" style={{ color: "var(--rc-link)" }}>{link}</a>
          {" - "}last 30 days: {row.bookings_30d} bookings, {row.codes_30d} codes sent ({Number(row.credits_30d)} credits)
        </div>
      )}
    </div>
  );
}
